/**
 * CallFlow Phone Numbers Tab Component
 *
 * Manage phone numbers in a CallFlow - add, remove (soft delete), view stats.
 */

import { useEffect, useState } from 'react';
import { callFlowService, CallFlowPhoneNumber } from '@/services/callFlowService';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/use-toast';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { supabase } from '@/supabase/supabase';

interface CallFlowPhonesTabProps {
  organizationId: string;
  callflowId: string;
  callflowStatus: string;
}

interface PhoneNumber {
  id: string;
  phone_number: string;
  type: string;
}

export function CallFlowPhonesTab({ callflowId, organizationId, callflowStatus }: CallFlowPhonesTabProps) {
  const [phones, setPhones] = useState<CallFlowPhoneNumber[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [availablePhones, setAvailablePhones] = useState<PhoneNumber[]>([]);
  const [selectedPhoneIds, setSelectedPhoneIds] = useState<string[]>([]);
  const [isAdding, setIsAdding] = useState(false);

  useEffect(() => {
    fetchPhones();
  }, [callflowId]);

  const fetchPhones = async () => {
    try {
      setIsLoading(true);
      const { phone_numbers: data, summary: summaryData } =
        await callFlowService.listPhoneNumbers(organizationId, callflowId);
      setPhones(data);
      setSummary(summaryData);
    } catch (error) {
      console.error('Failed to fetch phone numbers:', error);
      toast({
        title: 'Error',
        description: 'Failed to load phone numbers',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAvailablePhones = async () => {
    try {
      const { data, error } = await supabase
        .from('phone_numbers')
        .select('id, phone_number, type')
        .eq('organization_id', organizationId)
        .order('phone_number');

      if (error) throw error;

      // Filter out phones already in this CallFlow (active only)
      const existingPhoneIds = new Set(
        phones.filter(p => p.is_active).map(p => p.phone_number_id)
      );
      const available = (data || []).filter(p => !existingPhoneIds.has(p.id));
      setAvailablePhones(available);
    } catch (error) {
      console.error('Failed to fetch available phone numbers:', error);
      toast({
        title: 'Error',
        description: 'Failed to load available phone numbers',
        variant: 'destructive',
      });
    }
  };

  const handleOpenAddDialog = () => {
    setShowAddDialog(true);
    fetchAvailablePhones();
  };

  const handleAddPhones = async () => {
    if (selectedPhoneIds.length === 0) return;

    try {
      setIsAdding(true);
      await callFlowService.addPhoneNumbers(organizationId, callflowId, selectedPhoneIds);
      toast({
        title: 'Success',
        description: `Added ${selectedPhoneIds.length} phone number(s) to CallFlow`,
      });
      setSelectedPhoneIds([]);
      setShowAddDialog(false);
      await fetchPhones();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to add phone numbers',
        variant: 'destructive',
      });
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemovePhone = async (phoneNumberId: string) => {
    if (!confirm('Are you sure you want to deactivate this phone number?')) return;

    try {
      await callFlowService.removePhoneNumber(organizationId, callflowId, phoneNumberId);
      toast({
        title: 'Success',
        description: 'Phone number deactivated. In-progress calls will complete gracefully.',
      });
      await fetchPhones();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to remove phone number',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-5 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-gray-600">Total</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{summary.total}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-gray-600">Active</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-green-600">{summary.active}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-gray-600">Inactive</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-gray-600">{summary.inactive}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-gray-600">Total Calls</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{summary.total_calls}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-gray-600">Successful</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-green-600">
                {summary.successful_calls}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end">
        <Button onClick={handleOpenAddDialog}>
          <Plus className="h-4 w-4 mr-2" />
          Add Phone Numbers
        </Button>
      </div>

      {/* Phone Numbers Table */}
      <Card>
        <CardContent className="pt-6">
          {phones.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No phone numbers in this CallFlow. Add phone numbers to get started.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Phone Number</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Total Calls</TableHead>
                  <TableHead>Successful</TableHead>
                  <TableHead>Failed</TableHead>
                  <TableHead>Last Used</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {phones.map((phone) => (
                  <TableRow key={phone.id}>
                    <TableCell className="font-medium">
                      {phone.phone_numbers?.phone_number}
                    </TableCell>
                    <TableCell>
                      <Badge className={phone.phone_numbers?.type === 'inbound' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}>
                        {phone.phone_numbers?.type?.toUpperCase() || 'UNKNOWN'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {phone.is_active ? (
                        <Badge className="bg-green-100 text-green-800">ACTIVE</Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-800">INACTIVE</Badge>
                      )}
                    </TableCell>
                    <TableCell>{phone.total_calls}</TableCell>
                    <TableCell className="text-green-600">
                      {phone.successful_calls}
                    </TableCell>
                    <TableCell className="text-red-600">{phone.failed_calls}</TableCell>
                    <TableCell>
                      {phone.last_used_at
                        ? new Date(phone.last_used_at).toLocaleString()
                        : 'Never'}
                    </TableCell>
                    <TableCell>
                      {phone.is_active && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemovePhone(phone.phone_number_id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add Phone Numbers Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Phone Numbers to CallFlow</DialogTitle>
            <DialogDescription>
              Select phone numbers to add to this CallFlow
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {availablePhones.length === 0 ? (
              <p className="text-center text-gray-500 py-4">
                No available phone numbers to add
              </p>
            ) : (
              <div className="space-y-2">
                {availablePhones.map((phone) => (
                  <div
                    key={phone.id}
                    className="flex items-center gap-3 p-3 border rounded hover:bg-slate-800 cursor-pointer"
                    onClick={() => {
                      setSelectedPhoneIds((prev) =>
                        prev.includes(phone.id)
                          ? prev.filter((id) => id !== phone.id)
                          : [...prev, phone.id]
                      );
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedPhoneIds.includes(phone.id)}
                      onChange={() => {}}
                      className="h-4 w-4"
                    />
                    <div className="flex-1">
                      <p className="font-medium">{phone.phone_number}</p>
                      <p className="text-sm text-gray-600">
                        <Badge className={phone.type === 'inbound' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}>
                          {phone.type?.toUpperCase() || 'UNKNOWN'}
                        </Badge>
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-between items-center pt-4">
              <p className="text-sm text-gray-600">
                {selectedPhoneIds.length} phone number(s) selected
              </p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleAddPhones}
                  disabled={selectedPhoneIds.length === 0 || isAdding}
                >
                  {isAdding && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Add {selectedPhoneIds.length > 0 && `(${selectedPhoneIds.length})`}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
